import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';

export function TwoFactorSetupPage() {
  const [step, setStep] = useState<'init' | 'verify' | 'backup'>('init');
  const [qr, setQr] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const { refresh } = useAuth();

  const startSetup = async () => {
    setLoading(true);
    try {
      const res = await api.post<{ qrCode: string; secret: string }>('/auth/2fa/setup');
      setQr(res.qrCode);
      setSecret(res.secret);
      setStep('verify');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Fehler');
    } finally {
      setLoading(false);
    }
  };

  const activate = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await api.post<{ ok: boolean; backupCodes: string[] }>('/auth/2fa/activate', { code });
      setBackupCodes(res.backupCodes);
      setStep('backup');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Fehler');
    } finally {
      setLoading(false);
    }
  };

  const finish = async () => {
    await refresh();
    nav('/');
  };

  if (step === 'init') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="w-full max-w-sm card text-center space-y-4">
          <h1 className="text-xl font-bold">2FA einrichten</h1>
          <p className="text-sm text-slate-600">
            Zwei-Faktor-Authentifizierung ist Pflicht für das Admin-Dashboard.
            Du benötigst eine Authenticator-App (z.B. Google Authenticator, Authy).
          </p>
          {error && <div className="bg-red-50 text-red-700 text-sm rounded-md px-3 py-2">{error}</div>}
          <button onClick={startSetup} disabled={loading} className="btn-primary w-full">
            {loading ? 'Wird eingerichtet ...' : '2FA einrichten'}
          </button>
        </div>
      </div>
    );
  }

  if (step === 'verify') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="w-full max-w-sm card space-y-4">
          <h1 className="text-xl font-bold text-center">QR-Code scannen</h1>
          {qr && <img src={qr} alt="TOTP QR Code" className="mx-auto w-48 h-48" />}
          <p className="text-xs text-slate-500 text-center break-all">Manuell: {secret}</p>
          <form onSubmit={activate} className="space-y-4">
            {error && <div className="bg-red-50 text-red-700 text-sm rounded-md px-3 py-2">{error}</div>}
            <div>
              <label className="label">Code aus der App</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                className="input text-center text-lg tracking-widest"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                autoFocus
              />
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full">
              {loading ? 'Aktiviere ...' : 'Aktivieren'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-sm card space-y-4">
        <h1 className="text-xl font-bold text-center">Backup-Codes sichern</h1>
        <p className="text-sm text-slate-600 text-center">
          Diese Codes sind dein Notfall-Zugang. Speichere sie sicher (z.B. in Bitwarden).
        </p>
        <div className="bg-slate-50 border border-slate-200 rounded-md p-4 grid grid-cols-2 gap-2 font-mono text-sm">
          {backupCodes.map((c) => (
            <div key={c} className="text-center">{c}</div>
          ))}
        </div>
        <button onClick={finish} className="btn-primary w-full">
          Habe ich gesichert — Weiter
        </button>
      </div>
    </div>
  );
}
